import './Dashboard.css'

import React from 'react'
import { useCallback, useState, useRef, useEffect, useMemo, RefObject } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { MainLayout, MainPageLayout } from '@/components/layout/MainLayout'
import { Machine } from '@/services/model/machine'
import { useModal } from '@/components/modal/Modal'
import { MachinePath } from '../Common'
import { BackButton } from '@/components/layout/NavBar'
import { Spinner } from '@/components/spinner'
import { useAppContext } from '@/context/app'
import { ActivityStatusChart } from '@/pages/communal/statistics/components/statistic/ActivityStatusChart'
import { Roles } from '@/services/repository/users'
import { formatDate } from '@/common/time'
import { MachineName } from '@/components/machine'


type UUID = string;

interface CurrentUserDeviceSessionDTO {
  id: UUID;
  imei: string;
  ucid: string;

  /** ISO-8601 timestamp string, e.g. "2026-06-04T12:34:56.789Z" */
  startAt: string;

  userId: UUID | null;
  machineId: UUID | null;

  /** null when session is active (end_at IS NULL) */
  endAt: string | null;

  createdAt: string | null;

  /** jsonb payload from `device_sessions.statistic` */
  statistic: Record<string, unknown>;

  /**
   * numeric(20,0) from DB:
   * use string to avoid JS number precision issues.
   */
  deviceSessionId: string | null;
}

export const CommunalMyCurrentSession = () => {
  const { services, session, translates } = useAppContext()
  const [generalError, setGeneralError] = useState(false)
  const [noFoundError, setNoFoundError] = useState(false)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()


  const load = async () => {
    setLoading(true)
    setGeneralError(false)
    setNoFoundError(false)
    try {
      const response = await services.session.fetchWithToken(`/api/communal/sessions/current`, {})

      if (response.status === 404) {
        setNoFoundError(true)
        return
      }

      if (!response.ok) {
        setGeneralError(true)
        return
      }

      const session: CurrentUserDeviceSessionDTO = await response.json()
      navigate(`communal/machine/${session.machineId}/session/dashboard`)
    } catch {
      setGeneralError(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  if (!session || !translates) {
    return <Spinner />
  }

  return (
    <MainPageLayout
      pageName='communal-session-current-dashboard'
      services={services}
      translates={translates}
      session={session}
    >
      {generalError ? <div className='critic-error-box'>{translates?.for('errors.general_error')}</div> : null}
      {noFoundError ? <div className='critic-error-box'>{translates?.for('errors.no_current_session')}</div> : null}
      {loading ? <Spinner /> : null}
    </MainPageLayout>
  )
}
